// react
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ROUTES, DYNAMIC_ROUTES } from 'router';

// components
import {
  Dashboard as DashboardPage,
  DashboardRoutes,
} from '@karehero/llama/pwa/pages/Dashboard/Dashboard';

// hooks
import { useCareProfile } from 'hooks';
import { useActionPlan } from 'hooks';
import useIntercom from 'hooks/useIntercom';
import { useTracking, TrackingEvent } from 'hooks/useTracking';

// context
import { useTenant, Feature } from 'context';

// store
import { useDispatch, useSelector } from 'react-redux';
import {
  useBookmarkArticleMutation,
  useGetAllArticleBookmarkQuery,
  useGetAllSuggestedArticleQuery,
} from 'store/api/cms';
import { getAccount } from 'store/slices/iamSlice';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { switchCareCircle } from 'store/slices/careCircleSlice';
import { AppDispatch } from 'store';

const FALLBACK_SLUGS = {
  care: 'types-of-care-services-available-for-the-elderly',
  wellbeing: 'a-carer-s-guide-to-looking-after-yourself',
  careOptions:
    'how-to-compare-care-homes-and-evaluate-domiciliary-care-providers',
} as const;

const WELCOME_DESCRIPTION =
  'KareHero brings you real care experts by your side to help you find, fund and understand care.';

const DASHBOARD_ROUTES: DashboardRoutes = {
  financialStatus: ROUTES.FINANCIAL_STATUS,
  careAssessment: ROUTES.CARE_ASSESSMENT,
  powerOfAttorney: ROUTES.POWER_OF_ATTORNEY,
  eligibilityCalculator: ROUTES.ELIGIBILITY_CALCULATOR,
  organiser: ROUTES.ORGANISER,
  careArticle: DYNAMIC_ROUTES.LIBRARY_ARTICLE(FALLBACK_SLUGS.care),
  wellbeingArticle: DYNAMIC_ROUTES.LIBRARY_ARTICLE(FALLBACK_SLUGS.wellbeing),
  careOptionsArticle: DYNAMIC_ROUTES.LIBRARY_ARTICLE(
    FALLBACK_SLUGS.careOptions,
  ),
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const { actionPlanCategories } = useActionPlan();
  const { progressByID } = useCareProfile();
  const { data: articleBookmarks } = useGetAllArticleBookmarkQuery();
  const { data: suggestedArticles, isLoading: isArticlesLoading } =
    useGetAllSuggestedArticleQuery();
  const [bookmarkArticle] = useBookmarkArticleMutation();
  const account = useSelector(getAccount);
  const { showIntercom } = useIntercom();
  const { track } = useTracking();
  const {
    flags: { primaryCta },
  } = useFeatureFlags();
  const { tenant } = useTenant();
  const isAdmin = account?.accountRole.name === 'KareHero Administrator';

  const hasCareAssessment = tenant.features.includes(Feature.CARE_ASSESSMENT);
  const hasPoa = tenant.features.includes(Feature.POA);
  const hasBenefitsCalc = !!tenant.activeFeatureVersions.benefitsCalculator;

  const computedSuggestedArticles = useMemo(
    () => ({
      articles: suggestedArticles,
      bookmarkedArticles: articleBookmarks,
      onBookmark: (article: any) => bookmarkArticle(article._id),
      onArticleClick: (article: any) =>
        navigate(DYNAMIC_ROUTES.LIBRARY_ARTICLE(article?.slug?.current)),
      isLoading: isArticlesLoading,
    }),
    [
      articleBookmarks,
      suggestedArticles,
      bookmarkArticle,
      isArticlesLoading,
      navigate,
    ],
  );

  const powerOfAttorneyProgress = useMemo(() => {
    const gettingStarted = actionPlanCategories?.find(
      (c) => c.id === 'getting-started',
    );
    const poaItem = gettingStarted?.actionPlanItems?.find(
      (item) => item.id === 'set-up-power-of-attorney',
    );
    return poaItem?.isComplete ? 100 : 0;
  }, [actionPlanCategories]);

  const onCardClick = useCallback(
    (path: string, meta: Record<string, unknown>) => {
      try {
        track(TrackingEvent.DashboardCardClicked, {
          ...meta,
          hasCareAssessment,
          hasPoa,
          hasBenefitsCalc,
        });
      } catch (e) {
        console.error('Dashboard tracking failed', e);
      }
      navigate(path);
    },
    [track, navigate, hasCareAssessment, hasPoa, hasBenefitsCalc],
  );

  useEffect(() => {
    const careCircleId = searchParams.get('careCircleId');
    if (!careCircleId) return;

    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(careCircleId)) return;

    dispatch(switchCareCircle(careCircleId));
  }, [dispatch, searchParams]);

  return (
    <DashboardPage
      suggestedArticles={computedSuggestedArticles}
      account={account}
      description={WELCOME_DESCRIPTION}
      onBookCall={() => {
        try {
          track(TrackingEvent.DashboardBookCallClicked, {
            hasCareAssessment,
            hasPoa,
            hasBenefitsCalc,
          });
        } catch (e) {
          console.error('Dashboard tracking failed', e);
        }
        navigate(ROUTES.BOOK_CALL);
      }}
      onChat={() => {
        try {
          track(TrackingEvent.DashboardChatClicked, {
            hasCareAssessment,
            hasPoa,
            hasBenefitsCalc,
          });
        } catch (e) {
          console.error('Dashboard tracking failed', e);
        }
        showIntercom();
      }}
      primaryCta={
        primaryCta === 'chat' || primaryCta === 'both'
          ? primaryCta
          : 'book-a-call'
      }
      onToLibrary={() => navigate(ROUTES.LIBRARY)}
      onCardClick={onCardClick}
      hasCareAssessment={hasCareAssessment}
      hasPoa={hasPoa}
      hasBenefitsCalc={hasBenefitsCalc}
      showOrganiser={!isAdmin}
      routes={DASHBOARD_ROUTES}
      financialStatusProgress={progressByID['financial-status'] || 0}
      careAssessmentProgress={progressByID['care-assessment'] || 0}
      powerOfAttorneyProgress={powerOfAttorneyProgress}
    />
  );
};

export default Dashboard;
